<div class="capacity-management-container">
  <mat-card class="capacity-management-card">
    <!-- Header Grid Layout -->
    <div class="header-grid">
      <div class="title-cell">
        <h2>
          Capacity & Availability
          <span class="no-wrap"
            >Management
            <mat-icon
              class="header-info-icon"
              appHoverCard
              [cardWidth]="600"
              [placement]="'below'"
              (hoverShow)="onHeaderInfoShow($event)"
              (hoverHide)="onHeaderInfoHide()"
            >
              help
            </mat-icon></span
          >
        </h2>
      </div>

      <!-- Edit/Save/Cancel Icons - Top Right -->
      @if (!isReadOnly()) {
        <div class="edit-icon-cell">
          @if (isEditMode) {
            @if (isSaving) {
              <mat-spinner diameter="18"></mat-spinner>
            }
            @if (!isSaving) {
              <mat-icon
                class="header-icon"
                (click)="toggleEditMode()"
                matTooltip="Cancel"
                matTooltipPosition="below"
                >close</mat-icon
              >
              <mat-icon
                class="header-icon save-icon"
                (click)="saveAndExitEditMode()"
                [matTooltip]="getSaveButtonTooltip() || 'Save'"
                matTooltipPosition="below"
                [class.disabled]="
                  isSaving || isPeriodLoading || hasAnyValidationErrors()
                "
                >check</mat-icon
              >
            }
          } @else {
            <mat-icon
              class="header-icon"
              (click)="toggleEditMode()"
              matTooltip="Edit capacity"
              matTooltipPosition="below"
              >edit</mat-icon
            >
          }
        </div>
      }

      @if (!isReadOnly() && isEditMode && canEditCapacitySettings()) {
        <div class="interval-cell">
          <div class="interval-controls">
            @if (isInitialLoading || isCheckingOverallMode) {
              <!-- Overall Switch Skeleton Loader -->
              <div class="skeleton-toggle">
                <div
                  class="skeleton-line"
                  style="
                    width: 110px;
                    height: 28px;
                    border-radius: 14px;
                    margin-bottom: 0;
                  "
                ></div>
              </div>
            } @else {
              <div
                class="overall-switch-container"
                [matTooltip]="getEntryWindowTooltip()"
                matTooltipPosition="below"
              >
                <label class="overall-switch-label">
                  <input
                    type="checkbox"
                    class="overall-switch-input"
                    [checked]="isOverallMode"
                    [disabled]="
                      isSaving || isPeriodLoading || isEntryWindowClosed()
                    "
                    (change)="onOverallModeToggle($event)"
                  />
                  <span class="overall-switch-slider"></span>
                  <span class="overall-switch-text">Global</span>
                </label>
              </div>
            }
          </div>
        </div>
      }
      <!-- Skeleton loaders for datepicker during initial load -->
      @if (isInitialLoading) {
        <div class="datepicker-cell">
          <div
            class="skeleton-flex"
            style="gap: 16px; align-items: center; justify-content: flex-end"
          >
            <div
              class="skeleton-icon"
              style="width: 20px; height: 20px; border-radius: 50%"
            ></div>
            <div
              class="skeleton-stack"
              style="flex: 0 0 auto; align-items: center; gap: 2px"
            >
              <div
                class="skeleton-line"
                style="width: 100px; height: 11px; margin-bottom: 2px"
              ></div>
              <div
                class="skeleton-line"
                style="width: 240px; height: 14px; margin-bottom: 0"
              ></div>
            </div>
            <div
              class="skeleton-icon"
              style="width: 20px; height: 20px; border-radius: 50%"
            ></div>
            <div
              class="skeleton-line"
              style="width: 100px; height: 36px; border-radius: 4px"
            ></div>
          </div>
        </div>
      }
      @if (!isInitialLoading && (isReadOnly() || !isOverallMode)) {
        <div class="datepicker-cell">
          <button
            mat-icon-button
            (click)="onPreviousWeek()"
            [disabled]="!canNavigatePrevious()"
          >
            <mat-icon>chevron_left</mat-icon>
          </button>
          <div class="period-info">
            <div class="period-label">{{ getPeriodLabel() }}</div>
            <div class="period-display">
              {{ getCurrentDateRangeDisplay() }}
            </div>
          </div>
          <button mat-icon-button (click)="onNextWeek()" [disabled]="isSaving">
            <mat-icon>chevron_right</mat-icon>
          </button>
          <button
            mat-stroked-button
            (click)="returnToCurrentPeriod()"
            [disabled]="isSaving || isViewingCurrentPeriod()"
            class="return-to-current-button"
            [matTooltip]="
              isViewingCurrentPeriod()
                ? 'Already viewing current period'
                : 'Jump back to current period'
            "
          >
            <mat-icon>today</mat-icon>
            <span>Current</span>
          </button>
        </div>
      }
    </div>

    <!-- Overall Capacity Box (shown when in Overall mode) -->
    @if (isOverallMode && !isReadOnly() && isEditMode) {
      <div class="overall-capacity-section">
        <div class="overall-capacity-card">
          <div class="overall-capacity-header">
            <div class="overall-title">
              <svg
                class="status-dot-svg"
                width="12"
                height="12"
                viewBox="0 0 8 8"
                [matTooltip]="getStatusTooltipForStatus(overallCapacityStatus)"
                matTooltipPosition="above"
              >
                <circle
                  cx="4"
                  cy="4"
                  [attr.r]="
                    getStatusDotClassForStatus(overallCapacityStatus) ===
                    'status-dot-unknown'
                      ? 3
                      : 4
                  "
                  [attr.fill]="
                    getStatusDotClassForStatus(overallCapacityStatus) ===
                    'status-dot-unknown'
                      ? 'white'
                      : null
                  "
                  [attr.stroke]="
                    getStatusDotClassForStatus(overallCapacityStatus) ===
                    'status-dot-unknown'
                      ? '#9e9e9e'
                      : null
                  "
                  [attr.stroke-width]="
                    getStatusDotClassForStatus(overallCapacityStatus) ===
                    'status-dot-unknown'
                      ? 2
                      : null
                  "
                  class="status-dot"
                  [class]="getStatusDotClassForStatus(overallCapacityStatus)"
                />
              </svg>
              <h3>Overall Capacity Status</h3>
            </div>
            <div
              class="capacity-status-inline"
              [matTooltip]="getEntryWindowTooltip()"
              matTooltipPosition="above"
            >
              <mat-form-field appearance="outline" class="compact-select">
                <mat-select
                  placeholder="Select overall status"
                  [value]="overallCapacityStatus?.code"
                  [disabled]="isSaving || isEntryWindowClosed()"
                  (selectionChange)="onOverallCapacityChange($event.value)"
                >
                  @for (
                    option of selectableCapacityStatusOptions;
                    track option
                  ) {
                    <mat-option [value]="option.value">
                      {{ option.label }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Overall Capacity View (read-only or view mode) -->
    @if (overallCapacityStatus && (isReadOnly() || !isEditMode)) {
      <div class="overall-capacity-section">
        <div class="overall-capacity-card">
          <div class="overall-capacity-header">
            <div class="overall-title">
              <svg
                class="status-dot-svg"
                width="12"
                height="12"
                viewBox="0 0 8 8"
                [matTooltip]="getStatusTooltipForStatus(overallCapacityStatus)"
                matTooltipPosition="above"
              >
                <circle
                  cx="4"
                  cy="4"
                  [attr.r]="
                    getStatusDotClassForStatus(overallCapacityStatus) ===
                    'status-dot-unknown'
                      ? 3
                      : 4
                  "
                  [attr.fill]="
                    getStatusDotClassForStatus(overallCapacityStatus) ===
                    'status-dot-unknown'
                      ? 'white'
                      : null
                  "
                  [attr.stroke]="
                    getStatusDotClassForStatus(overallCapacityStatus) ===
                    'status-dot-unknown'
                      ? '#9e9e9e'
                      : null
                  "
                  [attr.stroke-width]="
                    getStatusDotClassForStatus(overallCapacityStatus) ===
                    'status-dot-unknown'
                      ? 2
                      : null
                  "
                  class="status-dot"
                  [class]="getStatusDotClassForStatus(overallCapacityStatus)"
                />
              </svg>
              <h3>Overall Capacity Status</h3>
            </div>
            <div class="overall-capacity-display">
              <span
                class="badge capacity-status-badge"
                [ngClass]="getStatusBadgeClassForStatus(overallCapacityStatus)"
              >
                {{ getStatusLabelForStatus(overallCapacityStatus) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Capacity Periods Grid (hidden when in Overall mode) -->
    @if (!isOverallMode) {
      <div class="capacity-periods-grid">
        <!-- Skeleton loaders for Edit Mode (taller cards with form fields) -->
        @if ((isInitialLoading || isPeriodLoading) && isEditMode) {
          <div class="skeleton-grid">
            @for (i of [0, 1, 2, 3]; track i) {
              <div class="period-card skeleton-card">
                <div class="skeleton-header" style="margin-bottom: 16px">
                  <div
                    class="skeleton-flex"
                    style="justify-content: space-between; align-items: center"
                  >
                    <div
                      class="skeleton-flex"
                      style="gap: 8px; align-items: center"
                    >
                      <div
                        class="skeleton-icon"
                        style="width: 12px; height: 12px; border-radius: 50%"
                      ></div>
                      <div
                        class="skeleton-line"
                        style="width: 160px; height: 20px; margin-bottom: 0"
                      ></div>
                    </div>
                    <div
                      class="skeleton-line"
                      style="
                        width: 220px;
                        height: 40px;
                        border-radius: 8px;
                        margin-bottom: 0;
                      "
                    ></div>
                  </div>
                </div>
                <div class="skeleton-content">
                  <div
                    class="skeleton-line"
                    style="
                      width: 100%;
                      height: 80px;
                      border-radius: 12px;
                      margin-bottom: 0;
                    "
                  ></div>
                  <div
                    style="
                      padding-top: 6px;
                      display: flex;
                      justify-content: flex-end;
                      padding-right: 16px;
                    "
                  >
                    <div
                      class="skeleton-line"
                      style="width: 100px; height: 16.5px; margin-bottom: 0"
                    ></div>
                  </div>
                </div>
              </div>
            }
          </div>
        }
        <!-- Skeleton loaders for View Mode (compact cards with just badge) -->
        <!-- Skeleton loaders for View Mode (compact cards with just badge) -->
        @if ((isInitialLoading || isPeriodLoading) && !isEditMode) {
          <div class="skeleton-grid">
            @for (i of [0, 1, 2, 3]; track i) {
              <div class="period-card skeleton-card skeleton-card-compact">
                <div class="period-header">
                  <div class="period-title">
                    <div
                      class="skeleton-icon"
                      style="width: 12px; height: 12px; border-radius: 50%"
                    ></div>
                    <div
                      class="skeleton-line"
                      style="width: 160px; height: 18px; margin-bottom: 0"
                    ></div>
                  </div>
                  <div style="display: flex; gap: 12px; align-items: center">
                    <!-- Billing hours skeleton -->
                    <div
                      class="skeleton-line"
                      style="width: 80px; height: 18px; margin-bottom: 0"
                    ></div>
                    <!-- Badge skeleton -->
                    <div
                      class="skeleton-line"
                      style="
                        width: 130px;
                        height: 34px;
                        border-radius: 8px;
                        margin-bottom: 0;
                      "
                    ></div>
                  </div>
                </div>
              </div>
            }
          </div>
        }
        <!-- Actual content -->
        @if (!isInitialLoading && !isPeriodLoading) {
          @for (
            period of periods;
            track period.year + "-" + period.weekNumber;
            let i = $index
          ) {
            <div
              class="period-card"
              [class.current-period-card]="isCurrentPeriod(period)"
              (click)="onSelectPeriod(period)"
              (focusin)="onCardFocusIn(period)"
              (focusout)="onCardFocusOut(period, $event, card)"
              #card
            >
              <!-- Period Header with Inline Capacity Status -->
              <div class="period-header">
                <div class="period-title">
                  <svg
                    class="status-dot-svg"
                    width="12"
                    height="12"
                    viewBox="0 0 8 8"
                    [matTooltip]="getStatusTooltip(period)"
                    matTooltipPosition="above"
                  >
                    @if (isMixedStatus(period)) {
                      <defs>
                        <linearGradient
                          [id]="getGradientId(period)"
                          x1="0%"
                          y1="0%"
                          x2="0%"
                          y2="100%"
                        >
                          @for (stop of getGradientStops(period); track stop) {
                            <stop
                              [attr.offset]="stop.offset"
                              [attr.stop-color]="stop.color"
                            />
                          }
                        </linearGradient>
                      </defs>
                    }
                    <circle
                      cx="4"
                      cy="4"
                      [attr.r]="
                        getStatusDotClass(period) === 'status-dot-unknown'
                          ? 3
                          : 4
                      "
                      [attr.fill]="
                        isMixedStatus(period)
                          ? 'url(#' + getGradientId(period) + ')'
                          : getStatusDotClass(period) === 'status-dot-unknown'
                            ? 'white'
                            : null
                      "
                      [attr.stroke]="
                        getStatusDotClass(period) === 'status-dot-unknown'
                          ? '#9e9e9e'
                          : null
                      "
                      [attr.stroke-width]="
                        getStatusDotClass(period) === 'status-dot-unknown'
                          ? 2
                          : null
                      "
                      class="status-dot"
                      [class]="getStatusDotClass(period)"
                    />
                  </svg>
                  <h4>
                    {{ period.displayText }}
                    @if (isCurrentPeriod(period)) {
                      <span class="current-period-text">
                        (current time period)</span
                      >
                    }
                  </h4>
                  @if (!isReadOnly() && isEditMode && isPeriodLocked(period)) {
                    <span
                      class="entry-window-closed"
                      [matTooltip]="entryWindowClosedMessage"
                      matTooltipPosition="above"
                    >
                      <mat-icon>lock</mat-icon>
                      Entry window closed
                    </span>
                  }
                </div>
                <!-- Capacity Status - Editable Mode -->
                @if (!isReadOnly() && isEditMode) {
                  <div
                    class="capacity-status-inline"
                    [matTooltip]="getPeriodLockTooltip(period)"
                    matTooltipPosition="above"
                  >
                    <!-- Expected Period Hours, to the left of the status it belongs to. Grouped
                             with its own message so the message stays under the field it describes
                             rather than under the pair. Placeholder rather than a label, matching
                             the select beside it. -->
                    <div class="expected-hours-group">
                      <mat-form-field
                        appearance="outline"
                        class="expected-hours-field"
                        subscriptSizing="dynamic"
                        [class.mat-form-field-invalid]="
                          hasExpectedHoursError(period)
                        "
                      >
                        <input
                          matInput
                          type="number"
                          inputmode="numeric"
                          min="0"
                          max="999"
                          step="1"
                          [value]="getExpectedHoursInput(period)"
                          [disabled]="isPeriodEditDisabled(period)"
                          [required]="expectedHoursRequired"
                          (input)="
                            onExpectedHoursChange(
                              period,
                              $any($event.target).value
                            )
                          "
                          [placeholder]="
                            expectedHoursRequired
                              ? 'Expected Hours *'
                              : 'Expected Hours'
                          "
                          [attr.aria-label]="
                            'Expected billable hours for ' + period.displayText
                          "
                          [attr.aria-invalid]="hasExpectedHoursError(period)"
                        />
                      </mat-form-field>
                      <!-- Rendered outside the form field, not as a <mat-error>. Material only
                               reveals a mat-error when the field owns an NgControl in an error
                               state, and this input is driven by raw [value]/(input) so that "" and
                               "abc" can be told apart. Without a control the mat-error is silently
                               never shown - the red outline would be the only feedback. -->
                      @if (hasExpectedHoursError(period)) {
                        <div
                          class="period-field-message period-field-message--error"
                          role="alert"
                        >
                          {{ getExpectedHoursError(period) }}
                        </div>
                      }
                    </div>
                    <!-- Grouped with its own message for the same reason as the hours field: the
                             message stays under the dropdown it describes, and the fixed-width group
                             keeps the row from shifting when it appears. -->
                    <div class="status-field-group">
                      <mat-form-field
                        appearance="outline"
                        class="compact-select"
                        [class.mat-form-field-invalid]="
                          hasValidationError(period)
                        "
                      >
                        <mat-select
                          [placeholder]="
                            isMixedStatus(period)
                              ? 'Mixed status'
                              : 'Select status'
                          "
                          [value]="
                            getCapacityForPeriod(period)?.capacityStatus?.code ||
                            ''
                          "
                          [disabled]="isPeriodEditDisabled(period)"
                          (selectionChange)="
                            onCapacityStatusChange(period, $event.value)
                          "
                        >
                          @for (
                            option of selectableCapacityStatusOptions;
                            track option
                          ) {
                            <mat-option [value]="option.value">
                              {{ option.label }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                      <!-- Outside the form field, not a <mat-error>: the select is driven by raw
                               [value]/(selectionChange) and owns no NgControl, so Material would never
                               reveal a mat-error here. -->
                      @if (getStatusFieldError(period); as statusError) {
                        <div
                          class="period-field-message period-field-message--error"
                          role="alert"
                        >
                          {{ statusError }}
                        </div>
                      }
                    </div>
                  </div>
                }
                <!-- Capacity Status - Read-Only Badge -->
                @if (isReadOnly() || !isEditMode) {
                  <div class="capacity-status-badge-wrapper">
                    <!-- The hours column: one flex child holding every hours line for the
                             period, top to bottom. The wrapper lays out as a row, so separate
                             blocks here would sit side by side rather than stack. -->
                    @if (showsHoursColumn(period)) {
                      <div class="hours-breakdown">
                        <!-- Expected Period Hours. On the current and future periods the figure
                                 stands in for the billed total: a resource who has reported hours
                                 for a period still to come has given the number that matters, and
                                 there is nothing to wait for. On a finished period it sits above
                                 the billed total instead, because there the forecast and the actual
                                 are both worth reading, one against the other. Only a finished
                                 period can reach the else branch: when the office requires hours
                                 and none were reported, the gap itself is shown. No caption line
                                 beneath the figure: the "h Expected" on it is what distinguishes it
                                 from the billed total. -->
                        @if (
                          shouldShowExpectedHours(period) ||
                          shouldShowPastExpectedHours(period)
                        ) {
                          @if (getExpectedHoursDisplay(period) !== null) {
                            <div class="total-hours expected-hours-total">
                              {{ getExpectedHoursDisplay(period) }}h Expected
                            </div>
                          } @else {
                            <div class="expected-hours-missing">
                              No expected hours reported
                            </div>
                          }
                        }
                        <!-- Skeleton loader for billing hours (only for current and past periods) -->
                        @if (showsBillingSkeleton(period)) {
                          <div class="total-hours">
                            <div class="skeleton-line medium"></div>
                          </div>
                        }
                        <!-- Actual billing hours data. Suppressed when expected hours are
                                 standing in for it on the current period: showing both there
                                 would leave two totals side by side and no way to tell which the
                                 period is being judged on. A finished period shows both on
                                 purpose, forecast first. -->
                        @if (showsBillingTotals(period)) {
                          <div class="total-hours">
                            {{ getBillingInfo(period).total }}h Total
                          </div>
                          @if (getBillingInfo(period).total > 0) {
                            <div
                              class="hours-details"
                              matTooltipPosition="above"
                              matTooltipClass="hours-details-tooltip"
                            >
                              <span class="hours-detail">
                                {{ getBillingInfo(period).billed }} Billable
                              </span>
                              <span class="hours-detail">/</span>
                              <span class="hours-detail">
                                {{ getBillingInfo(period).unbilled }}
                                Non-Billable
                              </span>
                              <span class="hours-detail">/</span>
                              <span class="hours-detail">
                                {{ getBillingInfo(period).proBono }} Pro Bono
                              </span>
                            </div>
                          }
                        }
                      </div>
                    }
                    <span
                      class="badge capacity-status-badge"
                      [ngClass]="getStatusBadgeClass(period)"
                      [style.--gradient-colors]="getBadgeGradient(period)"
                      [matTooltip]="getStatusTooltip(period)"
                      matTooltipPosition="above"
                    >
                      <span class="badge-text">{{
                        getStatusLabel(period)
                      }}</span>
                    </span>
                  </div>
                }
              </div>
              <!-- No separate read-only block for expected hours: outside edit mode they are
                       shown in the hours column above, standing in for the billed figure or
                       sitting above it, so a second copy down here would print the same number
                       twice. -->
              <!-- Notes Section - Editable -->
              @if (!isReadOnly() && isEditMode) {
                <div
                  class="period-form-section"
                  [matTooltip]="getPeriodLockTooltip(period)"
                  matTooltipPosition="above"
                >
                  <mat-form-field
                    appearance="outline"
                    class="full-width"
                    [class.mat-form-field-invalid]="hasNotesError(period)"
                    [class.at-limit-shake]="isNotesAtLimit(period)"
                  >
                    <textarea
                      matInput
                      [value]="getCapacityForPeriod(period)?.notes || ''"
                      [disabled]="isPeriodEditDisabled(period)"
                      (input)="onNotesChange(period, $any($event.target).value)"
                      (keydown)="onNotesKeydown(period, $event)"
                      maxlength="1000"
                      rows="2"
                      placeholder="Add optional details about your availability"
                    >
                    </textarea>
                    <mat-hint
                      align="end"
                      [class.at-limit]="isNotesAtLimit(period)"
                      class="character-count-hint"
                    >
                      {{ getNotesCharacterCount(period) }} / 1000
                    </mat-hint>
                  </mat-form-field>
                  <!-- Outside the form field for the same reason as the expected-hours message:
                           this textarea is bound with raw [value]/(input) and owns no NgControl, so
                           Material's error state is never true and a <mat-error> here was silently
                           never rendered.

                           Only reachable for notes loaded over the limit - typing cannot get here,
                           because onNotesChange truncates at 1000 and the textarea carries
                           maxlength. Hitting the limit while typing is signalled by the shake and
                           the character count instead. -->
                  @if (hasNotesError(period)) {
                    <div
                      class="period-field-message period-field-message--error"
                      role="alert"
                    >
                      Notes cannot exceed 1000 characters
                    </div>
                  } @else if (isNotesAtCharacterLimit(period)) {
                    <!-- A notice, not an error: 1000 characters is allowed, the user has just
                             run out of room. Without this the only signal that further typing is
                             being dropped is a counter that stops moving. role="status" rather
                             than "alert" for the same reason - it is information, not a failure. -->
                    <div
                      class="period-field-message period-field-message--notice"
                      role="status"
                    >
                      Character limit reached - 1000 of 1000
                    </div>
                  }
                </div>
              }
              <!-- Notes Section - Read-Only -->
              @if (
                (isReadOnly() || !isEditMode) &&
                getCapacityForPeriod(period)?.notes &&
                getCapacityForPeriod(period)?.notes?.trim()
              ) {
                <div class="period-notes-display">
                  <div class="notes-content">
                    {{ getCapacityForPeriod(period)?.notes }}
                  </div>
                </div>
              }
            </div>
          }
        }
      </div>
    }

    <!-- Status Legend - Below Capacity Periods -->
    <div class="status-legend-section">
      <div class="legend-text-wrapper">
        <span>Status Legend</span>
        <mat-icon
          class="legend-info-icon"
          appHoverCard
          [cardWidth]="1200"
          [cardHeight]="180"
          [placement]="'above'"
          (hoverShow)="onStatusLegendShow($event)"
          (hoverHide)="onStatusLegendHide()"
        >
          help
        </mat-icon>
      </div>
    </div>
  </mat-card>

  <!-- Header Info Hover Card -->
  @if (showHeaderInfo) {
    <div
      class="header-info-hover-card"
      [style.left.px]="headerInfoPosition.x"
      [style.top.px]="headerInfoPosition.y"
    >
      <mat-card class="hover-card-content">
        @if (!isReadOnly()) {
          <p class="header-info-text">
            Set your availability for upcoming periods to help Staffing Partners
            and PD Leads plan assignments effectively.
          </p>
        }
        @if (isReadOnly()) {
          <p class="header-info-text">
            View capacity and availability for upcoming periods.
          </p>
        }
      </mat-card>
    </div>
  }

  <!-- Status Legend Hover Card -->
  @if (showStatusLegend) {
    <div
      class="status-legend-hover-card"
      [style.left.px]="statusLegendPosition.x"
      [style.top.px]="statusLegendPosition.y"
    >
      <mat-card class="hover-card-content">
        <app-capacity-legend></app-capacity-legend>
      </mat-card>
    </div>
  }
</div>
